import {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FieldPath, FieldValues, PathValue } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { useRegisterSlideNavDirtyGetter } from '@/modules/presentation/clientPresentation/context/SlideNavGuard.context';

import { CoverSlide } from '../coverSlide/CoverSlide';
import { SlideEditorCoverPage } from '../SlideEditor.types';
import { useIsTemplate } from '../textEditors/SlideEditor.promptContext';
import { useSlideFieldLiveDirty } from './useSlideFieldLiveDirty';

export interface FormAwareCoverSlideProps<TShape extends FieldValues> {
  Slide?: ComponentProps<typeof CoverSlide>['Slide'];
  page: SlideEditorCoverPage['page'];
  bundle: SlideEditorCoverPage['bundle'];
  /** Form path the body content commits to. */
  customContentPath: FieldPath<TShape>;
  /** Optional form path the title commits to. */
  displayTitlePath?: FieldPath<TShape>;
  /** Optional form path the AI prompt commits to. */
  customPromptPath?: FieldPath<TShape>;
  HeaderComponent?: ReactNode;
  slideId?: string;
  footer?: ReactNode;
  highlightVariables?: boolean;
  /**
   * Reports whether the slide has in-progress (pre-blur) edits. Lets consumers
   * (the template editor) confirm before discarding mid-keystroke edits that
   * haven't yet committed to the form.
   */
  onUnsavedEditsChange?: (hasUnsavedEdits: boolean) => void;
}

/**
 * Form-aware {@link CoverSlide}: binds the title/body/prompt fields of any form
 * (`TShape`) to the slide given their field paths. Commits on blur with
 * `shouldDirty`/`shouldTouch` so the form's unsaved-changes blocker engages,
 * and arms that blocker for in-progress (pre-blur) edits via
 * {@link useSlideFieldLiveDirty}. The consumer maps the page/bundle and resolves
 * the field paths; everything else is shared.
 */
export function FormAwareCoverSlide<TShape extends FieldValues>({
  Slide,
  page,
  bundle,
  customContentPath,
  displayTitlePath,
  customPromptPath,
  HeaderComponent,
  slideId,
  footer,
  highlightVariables,
  onUnsavedEditsChange,
}: FormAwareCoverSlideProps<TShape>) {
  const { setValue } = useFormContext<TShape>();
  // Matches usePromptDisplayContent: the prompt is only the editable body (and
  // setting a prompt clears the content) in template mode. In live
  // presentations the editor always edits the content, prompt or not.
  const isTemplate = useIsTemplate();

  const commit = useCallback(
    (path: FieldPath<TShape>, value: string | null) => {
      // The caller types these paths to the slide's string text fields
      // (content/title/prompt), but TS can't prove the value type for a generic
      // TShape, so assert it. Not a path cast — the paths themselves are checked
      // at the call site.
      setValue(path, value as PathValue<TShape, typeof path>, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue]
  );

  // In template mode an active prompt makes the body editor hold the prompt
  // text, so compare the live body against the prompt; otherwise (and in live
  // presentations) compare against the page content.
  const bodySeed =
    isTemplate && page.customPrompt ? page.customPrompt : page.customContent;
  const {
    isDirty: titleDirty,
    onLiveChange: onTitleLiveChange,
    reset: resetTitle,
  } = useSlideFieldLiveDirty(page.displayTitle);
  const {
    isDirty: bodyDirty,
    onLiveChange: onBodyLiveChange,
    reset: resetBody,
  } = useSlideFieldLiveDirty(bodySeed);
  const hasUnsavedEdits = titleDirty || bodyDirty;
  // Browser-level navigation (refresh, close, history).
  useOnBeforeUnloadPrompt(hasUnsavedEdits);
  // Surface live-dirty so consumers can confirm before discarding even
  // mid-keystroke, before the editor blurs and commits to the form.
  useEffect(() => {
    onUnsavedEditsChange?.(hasUnsavedEdits);
    return () => onUnsavedEditsChange?.(false);
  }, [hasUnsavedEdits, onUnsavedEditsChange]);
  // Remounts the slide on discard (see handleDiscard) so the editors re-seed
  // their DOM from the committed form values.
  const [revertKey, setRevertKey] = useState(0);
  const handleDiscard = useCallback(() => {
    // Clear the live-dirty tracking AND remount the editors: they own their DOM
    // content uncontrolled, so without a remount a later blur could commit the
    // abandoned text even after the user chose "Discard changes".
    resetTitle();
    resetBody();
    setRevertKey((k) => k + 1);
  }, [resetTitle, resetBody]);
  // In-app slide-to-slide navigation in the presentation designer. No-op in the
  // template editor, which has no SlideNavGuard provider.
  useRegisterSlideNavDirtyGetter(hasUnsavedEdits, handleDiscard);

  const onCustomContentChange = useCallback(
    (value: string) => commit(customContentPath, value),
    [commit, customContentPath]
  );

  const onDisplayTitleChange = useCallback(
    (value: string) => {
      if (displayTitlePath) commit(displayTitlePath, value);
    },
    [commit, displayTitlePath]
  );

  const onPromptChange = useCallback(
    (newPrompt: string | null) => {
      if (!customPromptPath) return;
      commit(customPromptPath, newPrompt);
      // Template mode only: setting a prompt clears the body content (the
      // editor switches to editing the prompt). In live presentations the body
      // must be preserved — generation goes through an accept/reject step, so
      // clearing here could drop content if the user rejects or it fails.
      if (isTemplate && newPrompt) commit(customContentPath, '');
    },
    [commit, customPromptPath, customContentPath, isTemplate]
  );

  return (
    <CoverSlide
      key={revertKey}
      Slide={Slide}
      bundle={bundle}
      page={page}
      slideId={slideId}
      footer={footer}
      HeaderComponent={HeaderComponent}
      onCustomContentChange={onCustomContentChange}
      onContentGenerated={onCustomContentChange}
      onDisplayTitleChange={displayTitlePath ? onDisplayTitleChange : undefined}
      onPromptChange={customPromptPath ? onPromptChange : undefined}
      onCustomContentInput={onBodyLiveChange}
      onDisplayTitleInput={onTitleLiveChange}
      highlightVariables={highlightVariables}
    />
  );
}
